import { appStorage } from '../lib/betaStorage'
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { flushSync } from "react-dom";
import { JnrPageIcon, type JnrIconType } from "../components/JnrPageIcon";
import {
  NixiCompanion,
  type NixiActivity,
  type NixiAnimationState,
  type NixiColour,
  type NixiExpression,
  type NixiVariant,
} from "../components/NixiCompanion";
import { profilePhotoFor } from "../lib/profilePhotos";
import {
  loadNixiProfilePreferences,
  saveNixiProfilePreferences,
} from "../lib/nixiPreferences";
import {
  resolveJnrNixiPresentation,
  type JnrNixiTaskCategory,
} from "../lib/jnrNixiPresentation";
import { youthParticipantById } from "../lib/youthParticipants";
import "../jnr-interface.css";

type Screen =
  | "home"
  | "morning"
  | "daytime"
  | "help"
  | "break"
  | "feelings"
  | "regulation"
  | "rewards";
type Feeling = "Happy" | "Okay" | "Sad" | "Worried" | "Angry" | "Tired";
type TileTone = "lavender" | "mint" | "warm" | "blue" | "rose";
type TaskStep = {
  label: string;
  prompt: string;
  icon: JnrIconType;
  activity?: NixiActivity;
  /** Parent-selected category for personalised JNR prompts. */
  nixiCategory?: JnrNixiTaskCategory;
};
type JnrProgress = {
  points: number;
  bites: number;
  nixiName: string;
  colour: NixiColour;
  variant: NixiVariant;
  morningDone: number[];
  dayIndex: number;
};

function NixiStarMark({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 96 96"
      className={`nixi-star-mark ${className}`}
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="m48 7 11.2 27.5 29.5 2.2-22.6 19.2 7 28.8L48 69.2 22.9 84.7l7-28.8L7.3 36.7l29.5-2.2L48 7Z"
        fill="#F5C64F"
        stroke="#B87912"
        strokeWidth="4"
        strokeLinejoin="round"
      />
      <path
        d="M48 17 54.7 38 77 39.5 59.8 53.7 64.5 75 48 63.6 31.5 75l4.7-21.3L19 39.5 41.3 38 48 17Z"
        fill="#FFE99A"
        opacity=".72"
      />
      <path d="M31 34c5-8 13-13 22-14" fill="none" stroke="#FFFBE3" strokeWidth="5" strokeLinecap="round" opacity=".9" />
    </svg>
  );
}

function NixiCookieJar({ bites }: { bites: number }) {
  return (
    <div
      className="jnr-cookie-jar"
      role="img"
      aria-label={`${bites} NIXI bites in the cookie jar`}
    >
      <span className="jnr-cookie-jar__lid" aria-hidden="true" />
      <div className="jnr-cookie-jar__glass">
        <span className="jnr-cookie-jar__shine" aria-hidden="true" />
        <span className="jnr-cookie-jar__label" aria-hidden="true">
          <strong>NIXI</strong>
          <small>bites</small>
        </span>
        <span className="jnr-cookie-jar__cookies" aria-hidden="true">
          {Array.from({ length: Math.min(bites, 10) }, (_, index) => (
            <i key={index} className="jnr-cookie-jar__cookie" />
          ))}
        </span>
        <strong className="jnr-cookie-jar__count" aria-hidden="true">
          {bites}
        </strong>
      </div>
    </div>
  );
}

function NixiSoundVideo({
  src,
  className,
  label,
  poster,
  muted = false,
  loop = true,
  active = true,
  preload = "metadata",
  revealAfterPlaybackStarts = false,
  onEnded,
  onStarted,
  mediaRef,
}: {
  src: string;
  className: string;
  label: string;
  poster?: string;
  muted?: boolean;
  loop?: boolean;
  active?: boolean;
  preload?: "none" | "metadata" | "auto";
  revealAfterPlaybackStarts?: boolean;
  onEnded?: () => void;
  onStarted?: () => void;
  mediaRef?: { current: HTMLVideoElement | null };
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const attachVideo = useCallback((video: HTMLVideoElement | null) => {
          videoRef.current = video;
          if (video) {
            video.defaultMuted = muted;
            video.muted = muted;
            if (muted) video.setAttribute("muted", "");
            else video.removeAttribute("muted");
          }
          if (mediaRef) mediaRef.current = video;
  }, [muted, mediaRef]);
  const wasActiveRef = useRef(false);
  const [needsTap, setNeedsTap] = useState(false);
  const [hasVisibleFrame, setHasVisibleFrame] = useState(
    !revealAfterPlaybackStarts,
  );

  useEffect(() => {
    setHasVisibleFrame(!revealAfterPlaybackStarts);
  }, [active, revealAfterPlaybackStarts, src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = muted;
    if (video.readyState === HTMLMediaElement.HAVE_NOTHING) {
      video.load();
    }
    if (!active) {
      video.pause();
      wasActiveRef.current = false;
      return;
    }
    if (!wasActiveRef.current && video.currentTime > 0) video.currentTime = 0;
    wasActiveRef.current = true;
    const playAttempt = video.play();
    if (playAttempt) {
      playAttempt
        .then(() => setNeedsTap(false))
        .catch(() => {
          if (muted) {
            setNeedsTap(true);
            return;
          }

          // Safari can reject sound enabled playback even when the screen was
          // opened from a tap. Keep the animation moving instead of leaving an
          // empty stage, then let the next tap restore its sound.
          video.muted = true;
          void video.play().then(
            () => setNeedsTap(true),
            () => setNeedsTap(true),
          );
        });
    }
  }, [active, muted, preload, src]);

  const revealAndResume = (video: HTMLVideoElement) => {
    // Keep the real scene poster visible until Safari confirms that the video
    // is actually playing. iPhone Low Power Mode can load a video but refuse
    // autoplay, which would otherwise expose Safari's large native play icon.
    if (!revealAfterPlaybackStarts) setHasVisibleFrame(true);
    if (!active || !video.paused) return;
    video.muted = muted;
    void video.play().then(
      () => setNeedsTap(false),
      () => setNeedsTap(true),
    );
  };

  const playWithSound = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.error) video.load();
    if (video.ended || video.currentTime >= video.duration - 0.1) {
      video.currentTime = 0;
    }
    video.muted = muted;
    void video.play().then(
      () => setNeedsTap(false),
      () => setNeedsTap(true),
    );
  };

  return (
    <>
      <video
        ref={attachVideo}
        className={className}
        src={src}
        poster={poster}
        autoPlay={active}
        muted={muted}
        loop={loop}
        playsInline
        preload={preload}
        aria-label={label}
        aria-hidden={active ? undefined : true}
        style={
          revealAfterPlaybackStarts && !hasVisibleFrame
            ? { opacity: 0, visibility: "hidden" }
            : undefined
        }
        onClick={active ? playWithSound : undefined}
        onLoadedData={(event) => revealAndResume(event.currentTarget)}
        onCanPlay={(event) => revealAndResume(event.currentTarget)}
        onPlaying={() => {
          setHasVisibleFrame(true);
          onStarted?.();
        }}
        onPlay={() => setNeedsTap(false)}
        onTimeUpdate={(event) => {
          if (event.currentTarget.currentTime >= 0.55) {
            setHasVisibleFrame(true);
          }
        }}
        onError={() => setNeedsTap(true)}
        onEnded={onEnded}
      />
      {active && needsTap && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            playWithSound();
          }}
          className="jnr-video-sound-button"
          data-silent-speech="true"
          aria-label={`Play ${label}${muted ? "" : " with sound"}`}
        >
          <span aria-hidden="true">▶</span>
          {muted ? "Tap to play" : "Tap to play with sound"}
        </button>
      )}
    </>
  );
}

type RoutineVideoConfig = {
  src: string;
  label: string;
  className: string;
  poster?: string;
};

const COMPLETED_BEDTIME_VIDEO: RoutineVideoConfig = {
  src: "/nixi/nixi-bedtime-complete.mp4",
  label: "NIXI after finishing bedtime",
  className: "absolute inset-0 h-full w-full object-cover",
};

function routineVideoForStep(step: TaskStep | null): RoutineVideoConfig | null {
  if (!step) return null;
  if (step.label === "Bedtime") {
    return {
      src: "/nixi/nixi-bedtime-full-background-test.mp4",
      label: "NIXI in her bedroom getting ready for sleep",
      className: "absolute -top-[9.17%] left-0 h-auto w-full max-w-none",
    };
  }
  const videos: Partial<Record<NixiActivity, RoutineVideoConfig>> = {
    toilet: {
      src: "/nixi/nixi-toilet.mp4",
      label: "NIXI going to the toilet",
      className: "absolute inset-0 h-full w-full object-cover",
    },
    gettingDressed: {
      src: "/nixi/nixi-get-dressed.mp4",
      label: "NIXI getting dressed for the day",
      className: "absolute inset-0 h-full w-full object-cover",
    },
    eating: {
      src: "/nixi/nixi-breakfast.mp4",
      label: "NIXI having breakfast",
      className: "absolute inset-0 h-full w-full object-cover",
    },
    brushingTeeth: {
      src: "/nixi/nixi-brush-teeth.mp4",
      label: "NIXI brushing her teeth",
      className: "absolute inset-0 h-full w-full object-cover",
    },
    packingBag: {
      src: "/nixi/nixi-pack-bag.mp4",
      label: "NIXI packing her bag",
      className: "absolute inset-0 h-full w-full object-cover",
    },
    shoes: {
      src: "/nixi/nixi-shoes-on.mp4",
      label: "NIXI putting on her shoes",
      className: "absolute inset-0 h-full w-full object-cover",
    },
    preschool: {
      src: "/nixi/nixi-preschool.mp4",
      label: "NIXI getting ready for preschool",
      className: "absolute inset-0 h-full w-full object-cover",
    },
    snack: {
      src: "/nixi/nixi-snack.mp4",
      label: "NIXI having a snack",
      className: "absolute inset-0 h-full w-full object-cover",
    },
    play: {
      src: "/nixi/nixi-play.mp4",
      label: "NIXI ready to play",
      className: "absolute inset-0 h-full w-full object-cover",
    },
    dinner: {
      src: "/nixi/nixi-dinner.mp4",
      label: "NIXI having dinner",
      className: "absolute inset-0 h-full w-full object-cover",
    },
  };
  return step.activity ? videos[step.activity] ?? null : null;
}

function nixiHomeSpeechForHour(
  hour: number,
  finished: boolean,
  childName: string,
) {
  if (finished) {
    return {
      greeting: `Amazing work, ${childName}!`,
      message: "You finished your day",
    };
  }
  if (hour < 6) {
    return {
      greeting: `Rest well, ${childName}`,
      message: "Tomorrow is a brand new day",
    };
  }
  if (hour < 9) {
    return {
      greeting: `Good morning, ${childName}!`,
      message: "You can take today one step at a time",
    };
  }
  if (hour < 12) {
    return {
      greeting: "You’re doing so well!",
      message: "Keep going at your own pace",
    };
  }
  if (hour < 15) {
    return {
      greeting: `Great work, ${childName}!`,
      message: "It’s always okay to take a break",
    };
  }
  if (hour < 18) {
    return {
      greeting: "You’ve done so much today!",
      message: "Be proud of every little step",
    };
  }
  if (hour < 21) {
    return {
      greeting: "You did your best today!",
      message: "Now you can slow down and relax",
    };
  }
  return {
    greeting: `Goodnight, ${childName}`,
    message: "You are safe, loved and enough",
  };
}

const PARTICIPANT_ID = "u-jnr" as const;
const STORAGE_KEY = "marnixia.jnr.lizzie.progress.v3";
const LEGACY_STORAGE_KEY = "marnixia.jnr.lillian.progress.v2";
// Keep the customiser ready for a later build while pre-generated videos are used.
const SHOW_NIXI_CHOOSER_IN_PROTOTYPE = false;
const DEFAULT_PROGRESS: JnrProgress = {
  points: 4,
  bites: 4,
  nixiName: "NIXI",
  colour: "lavender",
  variant: "girl",
  morningDone: [],
  dayIndex: 0,
};

const MORNING_STEPS: TaskStep[] = [
  {
    label: "Toilet",
    prompt: "Let’s go to the toilet. I need to go too!",
    icon: "routine",
    activity: "toilet",
  },
  {
    label: "Breakfast",
    prompt: "Sit down and eat breakfast",
    icon: "breakfast",
    activity: "eating",
  },
  {
    label: "Get dressed",
    prompt: "Choose clothes and get dressed",
    icon: "getDressed",
    activity: "gettingDressed",
  },
  {
    label: "Brush teeth",
    prompt: "Brush the top and bottom teeth",
    icon: "brushTeeth",
    activity: "brushingTeeth",
  },
  {
    label: "Pack bag",
    prompt: "Put everything in the bag",
    icon: "backpack",
    activity: "packingBag",
  },
  {
    label: "Shoes on",
    prompt: "Put on socks and shoes",
    icon: "getDressed",
    activity: "shoes",
  },
];

const MORNING_COMPLETION_MESSAGES = [
  "Toilet finished. Great work!",
  "Breakfast finished. Well done!",
  "You’re dressed. Nice job!",
  "Teeth are brushed. Great work!",
  "Your bag is packed. Nice job!",
  "Shoes are on. Your morning routine is complete!",
] as const;

const DAY_STEPS: TaskStep[] = [
  {
    label: "Preschool",
    prompt: "It’s time for preschool!",
    icon: "school",
    activity: "preschool",
  },
  { label: "Snack", prompt: "Are you hungry too? Let’s have a snack!", icon: "snack", activity: "snack" },
  { label: "Play", prompt: "Then, play", icon: "play", activity: "play" },
  {
    label: "Dinner",
    prompt: "Then, dinner",
    icon: "dinner",
    activity: "dinner",
  },
  {
    label: "Bedtime",
    prompt: "Last, put on pajamas and cuddle your teddy",
    icon: "windDown",
    activity: "bedtime",
  },
];

const MY_DAY_STEPS: TaskStep[] = [...MORNING_STEPS, ...DAY_STEPS];

const tones: Record<TileTone, string> = {
  lavender: "border-[#D8C7FA] bg-[#FBF8FF]",
  mint: "border-[#BCEADF] bg-[#F5FCFA]",
  warm: "border-[#F3D993] bg-[#FFFDF5]",
  blue: "border-[#B8DFF2] bg-[#F6FBFE]",
  rose: "border-[#F3BFDD] bg-[#FFF7FC]",
};

function loadProgress(): JnrProgress {
  try {
    const preferences = loadNixiProfilePreferences(PARTICIPANT_ID);
    const saved =
      appStorage.getItem(STORAGE_KEY) ||
      appStorage.getItem(LEGACY_STORAGE_KEY) ||
      "{}";
    const value = JSON.parse(saved) as Partial<JnrProgress>;
    return {
      points: Number.isFinite(value.points)
        ? Number(value.points)
        : DEFAULT_PROGRESS.points,
      bites: Number.isFinite(value.bites)
        ? Number(value.bites)
        : DEFAULT_PROGRESS.bites,
      nixiName:
        typeof value.nixiName === "string" && value.nixiName.trim()
          ? value.nixiName.trim().slice(0, 18)
          : DEFAULT_PROGRESS.nixiName,
      colour: preferences.colour,
      variant: preferences.variant,
      morningDone: Array.isArray(value.morningDone)
        ? value.morningDone.filter(Number.isInteger)
        : [],
      dayIndex: Math.max(
        0,
        Math.min(DAY_STEPS.length, Number(value.dayIndex) || 0),
      ),
    };
  } catch {
    return DEFAULT_PROGRESS;
  }
}

function speakReadout(text: string, interrupt = true) {
  if (!("speechSynthesis" in window)) return;
  if (interrupt) window.speechSynthesis.cancel();
  const spokenText = text;
  const voice = new SpeechSynthesisUtterance(spokenText);
  voice.rate = 0.78;
  voice.pitch = 1.08;
  voice.volume = 1;
  window.speechSynthesis.speak(voice);
}

function playCrunch() {
  const AudioContextClass =
    window.AudioContext ||
    (window as typeof window & { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!AudioContextClass) return;
  const context = new AudioContextClass();
  const burst = (start: number, duration: number, frequency: number) => {
    const buffer = context.createBuffer(
      1,
      Math.ceil(context.sampleRate * duration),
      context.sampleRate,
    );
    const channel = buffer.getChannelData(0);
    for (let index = 0; index < channel.length; index += 1) {
      const envelope = Math.pow(1 - index / channel.length, 2);
      channel[index] = (Math.random() * 2 - 1) * envelope;
    }
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    source.buffer = buffer;
    filter.type = "bandpass";
    filter.frequency.value = frequency;
    filter.Q.value = 0.7;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.22, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    source.connect(filter).connect(gain).connect(context.destination);
    source.start(start);
    source.stop(start + duration);
  };
  const now = context.currentTime + 0.02;
  burst(now, 0.13, 1050);
  burst(now + 0.17, 0.12, 760);
  window.setTimeout(() => {
    void context.close();
  }, 500);
}

function PrimaryTile({
  icon,
  title,
  subtitle,
  tone,
  onClick,
  silentSpeech = false,
  className = "",
  children,
}: {
  icon?: JnrIconType;
  title: string;
  subtitle: string;
  tone: TileTone;
  onClick: () => void;
  silentSpeech?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={title}
      data-silent-speech={silentSpeech || undefined}
      className={`flex min-h-[148px] flex-col items-center justify-center rounded-[28px] border-2 p-4 text-center shadow-[0_8px_22px_rgba(65,48,96,.07)] transition active:scale-[.985] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-300 sm:min-h-[180px] ${tones[tone]} ${className}`}
    >
      <span className="relative grid h-[100px] w-full max-w-[156px] place-items-center overflow-hidden rounded-[24px] border border-white/80 bg-white shadow-[0_5px_14px_rgba(80,53,130,.08)] sm:h-[112px]">
        {children || (icon && <JnrPageIcon type={icon} size="medium" />)}
      </span>
      <strong className="mt-3 text-lg font-black tracking-tight text-[#312844] sm:text-xl">
        {title}
      </strong>
      <span className="mt-1 text-xs font-extrabold text-slate-500 sm:text-sm">
        {subtitle}
      </span>
    </button>
  );
}

type HomeTileScene =
  | "morning"
  | "daytime"
  | "help"
  | "break"
  | "feelings"
  | "nixi";

function NixiHomeTileArt({ scene }: { scene: HomeTileScene }) {
  if (scene === "nixi")
    return (
      <span
        className="jnr-nixi-home-tile relative block h-full w-full overflow-hidden bg-gradient-to-b from-violet-100 to-fuchsia-50"
        data-scene={scene}
        aria-hidden="true"
      >
        <img
          src="/nixi-face-tile.png"
          alt=""
          className="absolute inset-0 h-full w-full object-contain p-1"
          draggable={false}
        />
      </span>
    );

  const background =
    scene === "morning"
      ? "bg-gradient-to-b from-indigo-100 via-violet-50 to-amber-50"
      : scene === "daytime"
        ? "bg-gradient-to-b from-sky-100 to-amber-50"
        : scene === "help"
          ? "bg-gradient-to-b from-violet-50 to-pink-50"
          : scene === "break"
            ? "bg-gradient-to-b from-cyan-50 to-violet-50"
            : "bg-gradient-to-b from-rose-50 to-violet-50";

  return (
    <span
      className={`jnr-nixi-home-tile relative block h-full w-full overflow-hidden ${background}`}
      data-scene={scene}
      aria-hidden="true"
    >
      {scene === "morning" && (
        <img
          src="/nixi-morning-sleep-hat.png"
          alt=""
          className="absolute inset-0 h-full w-full object-contain p-1"
          draggable={false}
        />
      )}

      {scene === "daytime" && (
        <img
          src="/nixi-my-day-outfit.png"
          alt=""
          className="absolute left-1/2 top-1/2 h-[94%] w-[94%] -translate-x-1/2 -translate-y-1/2 object-contain"
          draggable={false}
        />
      )}

      {scene === "help" && (
        <img
          src="/nixi-help-adult-child.png"
          alt=""
          className="absolute left-1/2 top-1/2 h-[96%] w-[96%] -translate-x-1/2 -translate-y-1/2 object-contain"
          draggable={false}
        />
      )}

      {scene === "break" && (
        <img
          src="/nixi-break-headphones.png"
          alt=""
          className="absolute left-1/2 top-1/2 h-[94%] w-[94%] -translate-x-1/2 -translate-y-1/2 object-contain"
          draggable={false}
        />
      )}

      {scene === "feelings" && (
        <img
          src="/nixi-feelings-heart.png"
          alt=""
          className="absolute left-1/2 top-1/2 h-[96%] w-[96%] -translate-x-1/2 -translate-y-1/2 object-contain"
          draggable={false}
        />
      )}
    </span>
  );
}

function ProgressDots({ total, current }: { total: number; current: number }) {
  return (
    <div
      className="flex justify-center gap-2"
      aria-label={`Step ${Math.min(current + 1, total)} of ${total}`}
    >
      {Array.from({ length: total }, (_, index) => (
        <span
          key={index}
          className={`h-2.5 rounded-full ${index < current ? "w-5 bg-emerald-400" : index === current ? "w-8 bg-violet-600" : "w-2.5 bg-slate-200"}`}
        />
      ))}
    </div>
  );
}

export function JnrLillianPrototype() {
  const childProfile = youthParticipantById(PARTICIPANT_ID);
  const childName = childProfile.name.trim().split(/\s+/)[0] || "friend";
  const [screen, setScreen] = useState<Screen>("home");
  const [progress, setProgress] = useState<JnrProgress>(loadProgress);
  const [selectedFeeling, setSelectedFeeling] = useState<Feeling | null>(null);
  const [notice, setNotice] = useState("What would you like to do?");
  const [nixiState, setNixiState] = useState<NixiAnimationState>("wave");
  const [nixiExpression, setNixiExpression] = useState<NixiExpression>("happy");
  const [reactionActivity, setReactionActivity] = useState<NixiActivity | null>(
    null,
  );
  const [readoutsMuted, setReadoutsMuted] = useState(() => {
    try { return appStorage.getItem("marnixia.jnr.readoutsMuted") === "true"; }
    catch { return false; }
  });
  const readoutsMutedRef = useRef(readoutsMuted);
  const speak = (text: string, interrupt = true) => {
    if (!readoutsMutedRef.current) speakReadout(text, interrupt);
  };
  const toggleReadouts = () => {
    const muted = !readoutsMutedRef.current;
    readoutsMutedRef.current = muted;
    setReadoutsMuted(muted);
    if (muted && "speechSynthesis" in window) window.speechSynthesis.cancel();
    try { appStorage.setItem("marnixia.jnr.readoutsMuted", String(muted)); }
    catch { /* Keep the control usable when storage is unavailable. */ }
  };
  const [parentOpen, setParentOpen] = useState(false);
  const [hold, setHold] = useState(0);
  const [feeding, setFeeding] = useState(false);
  const [feedingBitesLeft, setFeedingBitesLeft] = useState(0);
  const [feedingBiteKey, setFeedingBiteKey] = useState(0);
  const [currentHour, setCurrentHour] = useState(() => new Date().getHours());
  const [routineTransitioning, setRoutineTransitioning] = useState(false);
  const [routinePreviousVideo, setRoutinePreviousVideo] =
    useState<RoutineVideoConfig | null>(null);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [nixiNameDraft, setNixiNameDraft] = useState("");
  const [nixiMove, setNixiMove] = useState(0);
  const [homeDaySceneComplete, setHomeDaySceneComplete] = useState(false);
  const [celebrationTheme, setCelebrationTheme] = useState<
    "stars" | "bedtime" | null
  >(null);
  const holdStart = useRef<number | null>(null);
  const holdFrame = useRef<number | null>(null);
  const reactionTimer = useRef<number | null>(null);
  const feedFinishTimer = useRef<number | null>(null);
  const feedResetTimer = useRef<number | null>(null);
  const feedVideoRef = useRef<HTMLVideoElement | null>(null);
  const homeSceneVideoRef = useRef<HTMLVideoElement | null>(null);
  const currentRoutineVideoRef = useRef<HTMLVideoElement | null>(null);
  const nextRoutineVideoRef = useRef<HTMLVideoElement | null>(null);
  const celebrationTimer = useRef<number | null>(null);
  const routineTransitionTimer = useRef<number | null>(null);
  const routineVideoFadeTimer = useRef<number | null>(null);
  const screenOpenRequest = useRef(0);
  const lastButtonVoice = useRef({ label: "", time: 0 });

  const morningIndex = MORNING_STEPS.findIndex(
    (_, index) => !progress.morningDone.includes(index),
  );
  const activeMorning = morningIndex < 0 ? null : MORNING_STEPS[morningIndex];
  const activeDay =
    progress.dayIndex < DAY_STEPS.length ? DAY_STEPS[progress.dayIndex] : null;
  const myDayIndex =
    morningIndex >= 0
      ? morningIndex
      : MORNING_STEPS.length + progress.dayIndex;
  const activeMyDay =
    myDayIndex < MY_DAY_STEPS.length ? MY_DAY_STEPS[myDayIndex] : null;
  const activeMyDayVideo = activeMyDay
    ? routineVideoForStep(activeMyDay)
    : myDayIndex >= MY_DAY_STEPS.length
      ? COMPLETED_BEDTIME_VIDEO
      : null;
  const nextMyDayVideo =
    myDayIndex + 1 < MY_DAY_STEPS.length
      ? routineVideoForStep(MY_DAY_STEPS[myDayIndex + 1])
      : activeMyDay?.label === "Bedtime"
        ? COMPLETED_BEDTIME_VIDEO
        : null;
  const routineVideoLayers: Array<{
    config: RoutineVideoConfig;
    role: "previous" | "current" | "preload";
  }> = [];
  if (routinePreviousVideo)
    routineVideoLayers.push({ config: routinePreviousVideo, role: "previous" });
  if (activeMyDayVideo)
    routineVideoLayers.push({ config: activeMyDayVideo, role: "current" });
  if (
    nextMyDayVideo &&
    nextMyDayVideo.src !== activeMyDayVideo?.src &&
    nextMyDayVideo.src !== routinePreviousVideo?.src
  )
    routineVideoLayers.push({ config: nextMyDayVideo, role: "preload" });
  const homeSceneOverride =
    typeof window === "undefined"
      ? null
      : new URLSearchParams(window.location.search).get("homeScene");
  const homeSpeechHour =
    homeSceneOverride === "morning"
      ? 8
      : homeSceneOverride === "day"
        ? 14
        : homeSceneOverride === "quiet"
          ? 18
          : homeSceneOverride === "night"
            ? 21
            : currentHour;
  const homeSpeech = nixiHomeSpeechForHour(
    homeSpeechHour,
    !activeMyDay,
    childName,
  );
  const homeSpeechText = `${homeSpeech.greeting} ${homeSpeech.message}`;
  const timedHomeScene =
    homeSceneOverride === "morning" ||
    homeSceneOverride === "day" ||
    homeSceneOverride === "quiet" ||
    homeSceneOverride === "night"
      ? homeSceneOverride
      : homeSceneOverride === "default"
        ? null
        : currentHour >= 5 && currentHour < 12
          ? "morning"
          : currentHour >= 12 && currentHour < 17
            ? "day"
            : currentHour >= 17 && currentHour < 19
              ? "quiet"
              : currentHour >= 19 || currentHour < 5
                ? "night"
                : null;
  const homeSceneVideo =
    timedHomeScene === "morning"
      ? "/nixi/nixi-home-morning.mp4"
      : timedHomeScene === "day"
        ? "/nixi/nixi-home-day.mov"
        : timedHomeScene === "quiet"
          ? "/nixi/nixi-home-quiet-time.mp4"
          : timedHomeScene === "night"
            ? "/nixi/nixi-home-night.mp4"
            : null;
  const daytimeHomeRotation =
    timedHomeScene === "morning" || timedHomeScene === "day";
  const activeHomeSceneVideo =
    daytimeHomeRotation && homeDaySceneComplete
      ? "/nixi/nixi-profile-play.mp4"
      : homeSceneVideo;
  const activeHomeScenePoster =
    !homeDaySceneComplete && timedHomeScene === "night"
      ? "/nixi/nixi-home-night-poster.jpg"
      : undefined;
  const showTimedHomeScene = Boolean(activeHomeSceneVideo);

  useEffect(() => setNixiNameDraft(progress.nixiName), [progress.nixiName]);

  useEffect(() => setHomeDaySceneComplete(false), [timedHomeScene]);

  useEffect(() => {
    const updateHour = () => setCurrentHour(new Date().getHours());
    const timer = window.setInterval(updateHour, 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const sources = Array.from(
      new Set(
        [
          activeMyDayVideo?.src,
          nextMyDayVideo?.src,
          activeHomeSceneVideo,
          "/nixi/nixi-profile-play.mp4",
          "/nixi/nixi-profile-feed.mp4",
        ].filter((src): src is string => Boolean(src)),
      ),
    );
    const preloadedVideos = sources.map((src) => {
      const video = document.createElement("video");
      video.preload = "auto";
      video.muted = true;
      video.playsInline = true;
      video.src = src;
      video.load();
      return video;
    });

    return () => {
      preloadedVideos.forEach((video) => {
        video.pause();
        video.removeAttribute("src");
        video.load();
      });
    };
  }, [activeHomeSceneVideo, activeMyDayVideo?.src, nextMyDayVideo?.src]);

  useEffect(() => {
    try {
      appStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
      saveNixiProfilePreferences(PARTICIPANT_ID, {
        variant: progress.variant,
        colour: progress.colour,
      });
    } catch {
      // Continue the routine even if this browser blocks persistent storage.
    }
  }, [progress]);
  useEffect(() => {
    if (screen === "morning" && activeMorning) speak(activeMorning.prompt);
    if (screen === "daytime" && activeMyDay) speak(activeMyDay.prompt);
  }, [screen, morningIndex, progress.dayIndex]);
  useEffect(() => {
    const timer = window.setTimeout(() => setNixiState("idle"), 1200);
    return () => {
      window.clearTimeout(timer);
      if (reactionTimer.current) window.clearTimeout(reactionTimer.current);
      if (feedFinishTimer.current) window.clearTimeout(feedFinishTimer.current);
      if (feedResetTimer.current) window.clearTimeout(feedResetTimer.current);
      if (celebrationTimer.current)
        window.clearTimeout(celebrationTimer.current);
      if (routineTransitionTimer.current)
        window.clearTimeout(routineTransitionTimer.current);
      if (routineVideoFadeTimer.current)
        window.clearTimeout(routineVideoFadeTimer.current);
      if (holdFrame.current) cancelAnimationFrame(holdFrame.current);
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    };
  }, []);
  useEffect(() => {
    if (screen !== "rewards" || feeding) return;
    const timer = window.setInterval(
      () => setNixiMove((value) => (value + 1) % 3),
      3600,
    );
    return () => window.clearInterval(timer);
  }, [screen, feeding]);

  const say = (message: string) => {
    setNotice(message);
    speak(message, false);
  };
  const announceButton = (target: EventTarget | null) => {
    const button = target instanceof Element ? target.closest("button") : null;
    if (
      !button ||
      button.hasAttribute("disabled") ||
      button.dataset.silentSpeech === "true"
    )
      return;
    const label = (
      button.getAttribute("aria-label") ||
      button.textContent ||
      ""
    )
      .replace(/\s+/g, " ")
      .trim();
    if (!label) return;
    const now = Date.now();
    if (
      lastButtonVoice.current.label === label &&
      now - lastButtonVoice.current.time < 650
    )
      return;
    lastButtonVoice.current = { label, time: now };
    speak(label);
  };
  const react = (
    message: string,
    activity: NixiActivity | null = null,
    celebration = false,
  ) => {
    if (reactionTimer.current) window.clearTimeout(reactionTimer.current);
    setReactionActivity(activity);
    setNixiState(celebration ? "celebrate" : "wingFlutter");
    setNixiExpression(celebration ? "proud" : "happy");
    setNotice(message);
    reactionTimer.current = window.setTimeout(
      () => {
        setReactionActivity(null);
        setNixiState("idle");
        setNixiExpression("encouraging");
      },
      activity === "bedtimeComplete" ? 10000 : celebration ? 1800 : 1300,
    );
  };

  const showCelebration = (theme: "stars" | "bedtime") => {
    if (celebrationTimer.current) window.clearTimeout(celebrationTimer.current);
    setCelebrationTheme(theme);
    celebrationTimer.current = window.setTimeout(
      () => setCelebrationTheme(null),
      2400,
    );
  };

  const feedNixi = () => {
    if ((!feeding && progress.bites < 5) || (feeding && feedingBitesLeft < 1))
      return;
    if (feedFinishTimer.current) window.clearTimeout(feedFinishTimer.current);
    if (feedResetTimer.current) window.clearTimeout(feedResetTimer.current);
    if (!feeding && feedVideoRef.current) {
      feedVideoRef.current.currentTime = 0;
      feedVideoRef.current.muted = false;
      void feedVideoRef.current.play().catch(() => undefined);
    }
    const bitesLeftAfterTap = feeding ? feedingBitesLeft - 1 : 4;
    setProgress((value) => ({ ...value, bites: Math.max(0, value.bites - 1) }));
    setFeedingBitesLeft(bitesLeftAfterTap);
    setFeedingBiteKey((value) => value + 1);
    if (!feeding) setFeeding(true);
    setNixiState("feed");
    setNixiExpression("excited");
    setNotice(
      bitesLeftAfterTap > 0
        ? `${bitesLeftAfterTap} bites left for NIXI.`
        : "NIXI is finishing her last bite.",
    );
    playCrunch();
    if (bitesLeftAfterTap === 0) {
      feedFinishTimer.current = window.setTimeout(() => {
        setFeeding(false);
        setFeedingBitesLeft(0);
        setNixiState("celebrate");
        setNixiExpression("proud");
        setNotice("Yum! Thank you!");
      }, 1100);
      feedResetTimer.current = window.setTimeout(() => {
        setNixiState("idle");
        setNixiExpression("encouraging");
      }, 2700);
    }
  };

  const holdRoutineVideoForFade = (step: TaskStep | null) => {
    const video = routineVideoForStep(step);
    if (!video) return;
    if (routineVideoFadeTimer.current)
      window.clearTimeout(routineVideoFadeTimer.current);
    routineVideoFadeTimer.current = null;
    setRoutinePreviousVideo(video);
  };

  const finishRoutineVideoFade = () => {
    if (!routinePreviousVideo || routineVideoFadeTimer.current) return;
    routineVideoFadeTimer.current = window.setTimeout(() => {
      setRoutinePreviousVideo(null);
      routineVideoFadeTimer.current = null;
    }, 460);
  };

  const startNextRoutineVideo = () => {
    const video = nextRoutineVideoRef.current;
    if (!video) return;
    video.muted = false;
    if (video.currentTime > 0) video.currentTime = 0;
    void video.play().catch(() => undefined);
  };

  const completeMorning = () => {
    if (morningIndex < 0 || routineTransitioning) return;
    const finished = morningIndex === MORNING_STEPS.length - 1;
    setRoutineTransitioning(true);
    react(
      MORNING_COMPLETION_MESSAGES[morningIndex] ?? "Great work!",
      activeMorning?.activity || null,
      finished,
    );
    startNextRoutineVideo();
    holdRoutineVideoForFade(activeMorning);
    setProgress((value) => ({
      ...value,
      morningDone: [...value.morningDone, morningIndex],
      points: value.points + 1,
      bites: value.bites + 1,
    }));
    routineTransitionTimer.current = window.setTimeout(() => {
      setRoutineTransitioning(false);
      if (finished) showCelebration("stars");
    }, 520);
  };

  const toggleDayStep = (index: number) => {
    if (routineTransitioning) return;
    const item = DAY_STEPS[index];
    if (index < progress.dayIndex) {
      setProgress((value) => ({ ...value, dayIndex: index }));
      react(`${item.label} is ready to do again.`);
      return;
    }
    // A future card is only a preview. It must never skip the current activity
    // (for example, tapping Snack must not jump straight to Play).
    if (index !== progress.dayIndex) {
      react(`Finish ${activeDay?.label ?? "the current activity"} first.`);
      return;
    }
    const next = index + 1;
    const earnsReward = index === progress.dayIndex;
    setRoutineTransitioning(true);
    react(
      next >= DAY_STEPS.length
        ? "Your day is finished. NIXI has pajamas and teddy ready for bed!"
        : `${item.label} is done.`,
      next >= DAY_STEPS.length ? "bedtimeComplete" : item.activity,
      next >= DAY_STEPS.length,
    );
    startNextRoutineVideo();
    holdRoutineVideoForFade(item);
    setProgress((value) => ({
      ...value,
      dayIndex: next,
      points: value.points + (earnsReward ? 1 : 0),
      bites: value.bites + (earnsReward ? 1 : 0),
    }));
    routineTransitionTimer.current = window.setTimeout(() => {
      setRoutineTransitioning(false);
      if (next >= DAY_STEPS.length)
        showCelebration(item.label === "Bedtime" ? "bedtime" : "stars");
    }, next >= DAY_STEPS.length ? 10200 : 520);
  };

  const toggleMyDayStep = (index: number) => {
    if (routineTransitioning) return;
    const item = MY_DAY_STEPS[index];
    if (index < myDayIndex) {
      react(`${item.label} is already done.`);
      return;
    }
    if (index > myDayIndex) {
      react(`Finish ${activeMyDay?.label ?? "the current activity"} first.`);
      return;
    }
    if (index < MORNING_STEPS.length) {
      completeMorning();
      return;
    }
    toggleDayStep(index - MORNING_STEPS.length);
  };

  const stopHold = () => {
    if (holdFrame.current) cancelAnimationFrame(holdFrame.current);
    holdFrame.current = null;
    holdStart.current = null;
    if (!parentOpen) setHold(0);
  };
  const beginHold = () => {
    holdStart.current = performance.now();
    const tick = (now: number) => {
      if (holdStart.current === null) return;
      const value = Math.min(100, ((now - holdStart.current) / 3000) * 100);
      setHold(value);
      if (value >= 100) {
        setParentOpen(true);
        stopHold();
        setHold(100);
        return;
      }
      holdFrame.current = requestAnimationFrame(tick);
    };
    holdFrame.current = requestAnimationFrame(tick);
  };
  const go = (next: Screen, message: string) => {
    screenOpenRequest.current += 1;
    // Mount the destination during the child's tap. This keeps the browser's
    // sound permission attached to the same gesture and removes the blank wait
    // between screens. Each destination video still preloads its next scene.
    flushSync(() => setScreen(next));
    setNotice(message);

    if (next === "daytime") {
      const video = currentRoutineVideoRef.current;
      if (!video) return;
      video.muted = false;
      if (video.currentTime > 0) video.currentTime = 0;
      void video.play().catch(() => undefined);
    }
  };
  const openNixiProfile = () => {
    go("rewards", "My NIXI");
  };

  const playHomeScene = () => {
    const video = homeSceneVideoRef.current;
    if (!video) return;
    video.defaultMuted = true;
    video.muted = true;
    if (video.ended) video.currentTime = 0;
    void video.play().catch(() => undefined);
  };

  const BackButton = () =>
    screen === "home" ? null : (
      <button
        type="button"
        onClick={() => go("home", "Home")}
        className="inline-flex min-h-12 items-center gap-2 rounded-2xl border-2 border-violet-200 bg-white px-4 font-black text-violet-800 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-300"
      >
        <JnrPageIcon type="back" size="small" /> Home
      </button>
    );
  const openLillianProfile = () => {
    setParentOpen(false);
    setHold(0);
    appStorage.setItem("schub.currentUserId", "u-lillian");
    window.location.hash = "/youth-workspace";
    window.location.reload();
  };

  const ParentControl = () => (
    <button
      type="button"
      data-silent-speech="true"
      onKeyDown={(event) => {
        if ((event.key === " " || event.key === "Enter") && !event.repeat) {
          event.preventDefault();
          beginHold();
        }
      }}
      onKeyUp={stopHold}
      onBlur={stopHold}
      onPointerDown={beginHold}
      onPointerUp={stopHold}
      onPointerLeave={stopHold}
      onPointerCancel={stopHold}
      aria-label="Parent controls. Hold for three seconds"
      className="relative min-h-12 min-w-[92px] overflow-hidden rounded-2xl border-2 border-violet-200 bg-violet-50 px-3 text-xs font-black text-violet-800 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-300"
    >
      <span>Parent</span>
      <span className="block text-[10px] text-violet-500">Hold 3 sec</span>
      <span className="absolute inset-x-0 bottom-0 h-1 bg-violet-100">
        <span
          className="block h-full bg-violet-600"
          style={{ width: `${hold}%` }}
        />
      </span>
    </button>
  );

  const Header = (
    <header className="jnr-profile-header flex flex-wrap items-center gap-3 rounded-[25px] border border-violet-100 bg-white px-4 py-3 shadow-[0_8px_24px_rgba(62,48,98,.06)]">
      <img
        src={profilePhotoFor("u-jnr") || ""}
        alt={`${childName} profile`}
        className="h-12 w-12 shrink-0 rounded-full border-2 border-violet-200 object-cover shadow-sm"
      />
      <div className="min-w-0 flex-1">
        <h1 className="text-xl font-black text-[#3A285C] sm:text-2xl">
          Hi {childName}
        </h1>
        <p className="truncate text-xs font-bold text-slate-500 sm:text-sm">
          {notice}
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={toggleReadouts}
          data-silent-speech="true"
          aria-pressed={readoutsMuted}
          aria-label={readoutsMuted ? "Turn reading aloud on. Video sound stays on." : "Mute reading aloud. Video sound stays on."}
          title="Parent reading control. Does not mute videos."
          className="min-h-12 rounded-2xl border-2 border-violet-200 bg-violet-50 px-3 text-xs font-black text-violet-800 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-300"
        >
          Reading {readoutsMuted ? "off" : "on"}
          <span className="block text-[10px] text-violet-500">Parent · tap</span>
        </button>
        {ParentControl()}
      </div>
    </header>
  );

  const Home = (
    <section className="jnr-home-screen flex-1 gap-4">
      <div
        className={`jnr-nixi-home-banner relative grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 overflow-hidden rounded-[24px] border border-violet-100 bg-gradient-to-r from-[#F7F1FF] to-[#FFF5FB] px-4 py-3 ${showTimedHomeScene ? "jnr-nixi-home-banner--scene" : ""}`}
      >
        <button
          type="button"
          className="jnr-home-nixi-speech"
          onClick={() => say(homeSpeechText)}
          aria-label={`${homeSpeechText} Tap to hear NIXI.`}
        >
          <strong>{homeSpeech.greeting}</strong>
          <span>
            {homeSpeech.message}
            <NixiStarMark className="nixi-star-mark--speech" />
          </span>
        </button>
        {showTimedHomeScene && activeHomeSceneVideo ? (
          <div
            className="jnr-home-time-scene col-span-2"
            onPointerDown={playHomeScene}
          >
            {activeHomeScenePoster && (
              <img
                src={activeHomeScenePoster}
                alt=""
                className="jnr-home-time-scene__backdrop"
                aria-hidden="true"
              />
            )}
            <NixiSoundVideo
              src={activeHomeSceneVideo}
              className="jnr-home-time-scene__video"
              label={
                homeDaySceneComplete
                  ? `${progress.nixiName} playing on the Home screen`
                  : `${progress.nixiName} ${timedHomeScene} scene. Tap to replay.`
              }
              poster={activeHomeScenePoster}
              muted
              loop={!daytimeHomeRotation || homeDaySceneComplete}
              preload="auto"
              revealAfterPlaybackStarts
              mediaRef={homeSceneVideoRef}
              onEnded={
                daytimeHomeRotation && !homeDaySceneComplete
                  ? () => setHomeDaySceneComplete(true)
                  : undefined
              }
            />
          </div>
        ) : (
          <NixiCompanion
            variant={progress.variant}
            colour={progress.colour}
            state="idle"
            expression="happy"
            size="medium"
            context="home"
            className="col-span-2 justify-self-start !h-48 !w-48 sm:!h-56 sm:!w-56 md:!h-80 md:!w-80"
          />
        )}
        <div className="jnr-home-rewards-bar jnr-home-rewards-panel">
          <p className="text-xs font-black uppercase tracking-[.14em] text-violet-700">
            Your NIXI rewards
          </p>
          <p className="jnr-home-reward-values flex flex-wrap justify-end gap-x-3 gap-y-0.5 text-sm font-black text-violet-950">
            <span className="inline-flex items-center whitespace-nowrap">
              <NixiStarMark className="nixi-star-mark--reward" />
              {progress.points} points
            </span>
            <span className="whitespace-nowrap">🍪 {progress.bites} NIXI bites</span>
          </p>
        </div>
      </div>
      <div
        className="jnr-home-choice-grid grid flex-1 grid-cols-2 gap-3 md:gap-4"
        aria-label={`${childName}’s home choices`}
      >
        <PrimaryTile
          className="jnr-home-primary-day"
          title="My Day"
          subtitle="What is next?"
          tone="warm"
          onClick={() =>
            go(
              "daytime",
              activeMyDay ? activeMyDay.prompt : "Your day is finished.",
            )
          }
        >
          <NixiHomeTileArt scene="morning" />
        </PrimaryTile>
        <PrimaryTile
          title="I Need Help"
          subtitle="Tell someone"
          tone="rose"
          onClick={() => go("help", "Choose what you need to say.")}
        >
          <NixiHomeTileArt scene="help" />
        </PrimaryTile>
        <PrimaryTile
          title="I Need a Break"
          subtitle="Help my body"
          tone="mint"
          onClick={() => go("break", "Choose what can help your body.")}
        >
          <NixiHomeTileArt scene="break" />
        </PrimaryTile>
        <PrimaryTile
          title="Feelings"
          subtitle="How I feel"
          tone="rose"
          onClick={() => go("feelings", "How do you feel?")}
        >
          <NixiHomeTileArt scene="feelings" />
        </PrimaryTile>
        <PrimaryTile
          title="My NIXI"
          subtitle={`${progress.points} points`}
          tone="lavender"
          onClick={openNixiProfile}
          silentSpeech
        >
          <NixiHomeTileArt scene="nixi" />
        </PrimaryTile>
      </div>
    </section>
  );

  const TaskBodyDouble = ({
    title,
    subtitle,
    step,
    onDone,
    finished,
    onReset,
  }: {
    title: string;
    subtitle: string;
    step: TaskStep | null;
    onDone: () => void;
    finished: boolean;
    onReset?: () => void;
  }) => {
    const promptPresentation = resolveJnrNixiPresentation(
      step?.prompt || step?.label || "",
      step?.nixiCategory,
    );
    const activity =
      (reactionActivity === "flyComplete" ? "dressed" : reactionActivity) ||
      step?.activity ||
      promptPresentation.activity ||
      (progress.morningDone.includes(2) ? "dressed" : "none");
    const actionPrompt = finished
      ? "All done!"
      : step?.activity === "toilet"
        ? "Let’s go to the toilet. I need to go too!"
        : step?.activity === "gettingDressed"
          ? "I want to get dressed for the day, let’s do it together!"
          : step?.activity === "brushingTeeth"
            ? "Having clean teeth is the best way to start the day."
            : step?.activity === "eating"
              ? "Yum! Breakfast time, I am hungry!"
              : step?.activity === "packingBag"
                ? "Let’s pack our bag, I don’t want to forget anything!"
                : step?.activity === "shoes"
                  ? "We are almost ready! Now we put our shoes on!"
                  : step?.label === "Bedtime"
                    ? "Let’s get ready for sleep!"
                  : step?.prompt || promptPresentation.message;
    return (
      <section className="flex flex-1 flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <BackButton />
          <div className="text-right">
            <h2 className="text-2xl font-black text-[#34264A]">{title}</h2>
            <p className="text-sm font-bold text-slate-500">{subtitle}</p>
          </div>
        </div>
        <div className="grid flex-1 gap-4 md:grid-cols-[1.05fr_.95fr]">
          <div className={`relative flex flex-col items-center justify-center overflow-hidden rounded-[30px] border-2 border-violet-100 bg-gradient-to-b from-[#FBF8FF] to-[#F5FCFA] text-center shadow-[0_10px_28px_rgba(68,49,104,.07)] ${step?.activity === "gettingDressed" || step?.activity === "eating" ? "aspect-[55/67] min-h-0 self-start bg-[#dcefff]" : "min-h-[320px] p-5"}${step?.activity === "toilet" ? " jnr-nixi-toilet-stage" : ""}`}>
            {step?.activity === "toilet" && (
              <div className="jnr-bathroom-door" aria-hidden="true">
                <span className="jnr-bathroom-door__panel">
                  <span className="jnr-bathroom-door__sign">Bathroom</span>
                  <i />
                </span>
              </div>
            )}
            {step?.activity === "gettingDressed" && !finished ? (
              <NixiSoundVideo
                className="absolute inset-0 h-full w-full object-cover"
                src="/nixi/nixi-get-dressed.mp4"
                label="NIXI getting dressed for the day"
                preload="auto"
              />
            ) : step?.activity === "eating" && !finished ? (
              <NixiSoundVideo
                className="absolute inset-0 h-full w-full object-cover"
                src="/nixi/nixi-breakfast.mp4"
                label="NIXI having breakfast"
                preload="auto"
              />
            ) : (
              <NixiCompanion
                variant={progress.variant}
                state={nixiState}
                expression={finished ? "proud" : "focused"}
                size="large"
                context="task"
                colour={progress.colour}
                activity={finished ? "flyComplete" : activity}
                className={finished ? "jnr-nixi-morning-complete" : ""}
              />
            )}
            <div className={`z-10 max-w-sm rounded-[22px] bg-white/95 px-5 py-3 shadow-sm ${(step?.activity === "gettingDressed" || step?.activity === "eating") && !finished ? "absolute bottom-5 left-4 right-4 mx-auto backdrop-blur-sm" : "relative mt-2"}`}>
              <p className="text-sm font-black text-violet-700">{actionPrompt}</p>
            </div>
          </div>
          <div className="flex min-h-[320px] flex-col items-center justify-center rounded-[30px] border-2 border-slate-100 bg-white p-6 text-center shadow-[0_10px_28px_rgba(68,49,104,.06)]">
            {step ? (
              <>
                <span className="grid h-28 w-28 place-items-center rounded-[28px] bg-[#F7F3FE]">
                  <JnrPageIcon type={step.icon} size="large" />
                </span>
                <p className="mt-5 text-sm font-black uppercase tracking-[.14em] text-violet-500">
                  Now
                </p>
                <h3 className="mt-1 text-3xl font-black text-slate-900">
                  {step.label}
                </h3>
                <p className="mt-2 max-w-xs text-base font-bold text-slate-500">
                  {step.prompt}
                </p>
                <button
                  type="button"
                  onClick={onDone}
                  disabled={routineTransitioning}
                  className="mt-6 min-h-16 w-full max-w-sm rounded-[22px] bg-violet-700 text-xl font-black text-white shadow-[0_8px_18px_rgba(91,60,170,.22)] disabled:cursor-wait disabled:opacity-70"
                >
                  Done
                </button>
              </>
            ) : (
              <>
                <JnrPageIcon type="rewards" size="large" />
                <h3 className="mt-4 text-3xl font-black text-emerald-800">
                  All done!
                </h3>
                <p className="mt-2 font-bold text-slate-500">
                  You and NIXI finished together.
                </p>
                {onReset && (
                  <button
                    type="button"
                    onClick={onReset}
                    className="mt-6 min-h-12 rounded-2xl border-2 border-violet-200 bg-violet-50 px-5 font-black text-violet-800"
                  >
                    Start again
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </section>
    );
  };

  const Morning = (
    <TaskBodyDouble
      title="Morning"
      subtitle={
        morningIndex < 0
          ? "Finished"
          : `Step ${morningIndex + 1} of ${MORNING_STEPS.length}`
      }
      step={activeMorning}
      onDone={completeMorning}
      finished={morningIndex < 0}
      onReset={() => {
        setProgress((value) => ({ ...value, morningDone: [] }));
        setNotice("Morning is ready to start again.");
      }}
    />
  );

  const Daytime = (
    <section className="flex flex-1 flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <BackButton />
        <div className="text-right">
          <h2 className="text-2xl font-black text-[#34264A]">My Day</h2>
          <p className="text-sm font-bold text-slate-500">
            Tap the next step to mark it done.
          </p>
        </div>
      </div>
      <ProgressDots total={MY_DAY_STEPS.length} current={myDayIndex} />
      <div className="jnr-day-grid grid min-w-0 flex-1 gap-4 md:grid-cols-[.9fr_1.1fr]">
        <div className={`relative flex flex-col items-center justify-center overflow-hidden rounded-[30px] border-2 border-violet-100 bg-[#eadfd7] p-4 text-center ${activeMyDay?.label === "Bedtime" ? "aspect-[440/491] min-h-0 self-start" : activeMyDayVideo ? "aspect-[55/67] min-h-0 self-start" : "min-h-[280px]"}`}>
          {activeMyDayVideo ? (
            <>
              {routineVideoLayers.map(({ config, role }) => (
                <NixiSoundVideo
                  key={config.src}
                  className={`${config.className} jnr-routine-video-layer jnr-routine-video-layer--${role}${role === "current" && !routinePreviousVideo ? " jnr-routine-video-layer--instant" : ""}`}
                  src={config.src}
                  label={config.label}
                  poster={role === "current" ? undefined : config.poster}
                  active={role !== "preload"}
                  muted={role !== "current"}
                  preload="auto"
                  revealAfterPlaybackStarts={role === "current"}
                  onStarted={role === "current" ? finishRoutineVideoFade : undefined}
                  mediaRef={
                    role === "current"
                      ? currentRoutineVideoRef
                      : role === "preload"
                        ? nextRoutineVideoRef
                        : undefined
                  }
                />
              ))}
            </>
          ) : (
            <NixiCompanion
              variant={progress.variant}
              state={nixiState}
              expression={activeMyDay ? "focused" : "proud"}
              size="large"
              context="task"
              colour={progress.colour}
              activity={
                reactionActivity ||
                activeMyDay?.activity ||
                (myDayIndex >= MY_DAY_STEPS.length ? "bedtime" : "none")
              }
              className={myDayIndex >= MY_DAY_STEPS.length ? "jnr-nixi-bedtime-flyaway" : ""}
            />
          )}
          {activeMyDay && (
            <p className={`z-10 rounded-2xl px-4 py-3 text-sm font-black text-violet-900 ${activeMyDayVideo ? "absolute bottom-5 left-4 right-4 mx-auto max-w-sm bg-white/95 shadow-sm backdrop-blur-sm" : "relative mt-2 bg-white/95 shadow-sm"}`}>
              {activeMyDay.label === "Bedtime"
                ? `Goodnight, ${childName}. See you tomorrow!`
                : activeMyDay.label === "Preschool"
                  ? "It’s time for preschool!"
                  : activeMyDay.label === "Snack"
                    ? "Are you hungry too? Let’s have a snack!"
                    : activeMyDay.label === "Play"
                      ? "Let’s play together!"
                      : activeMyDay.prompt}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2 rounded-[30px] border-2 border-slate-100 bg-white p-3 shadow-[0_10px_28px_rgba(68,49,104,.06)]">
          {MY_DAY_STEPS.map((item, index) => {
            const done = index < myDayIndex;
            const locked = index > myDayIndex;
            return (
              <button
                key={item.label}
                type="button"
                aria-label={`${item.label}. ${done ? "Done." : locked ? "Locked until the previous step is done." : "Tap to mark done."}`}
                onClick={() => toggleMyDayStep(index)}
                disabled={routineTransitioning || locked}
                className={`flex min-h-[66px] items-center gap-3 rounded-[19px] border-2 px-3 text-left transition active:scale-[.99] disabled:cursor-not-allowed ${done ? "border-emerald-100 bg-emerald-50" : index === myDayIndex ? "border-violet-300 bg-violet-50" : "border-transparent bg-slate-50 opacity-60"}`}
              >
                <span
                  className={`grid h-9 w-9 place-items-center rounded-full text-sm font-black ${done ? "bg-emerald-500 text-white" : index === myDayIndex ? "bg-violet-700 text-white" : "bg-white text-slate-400"}`}
                >
                  {done ? "✓" : index + 1}
                </span>
                <JnrPageIcon type={item.icon} size="small" />
                <strong className="flex-1 text-base text-slate-800">
                  {item.label}
                </strong>
                <span
                  className={`rounded-xl px-3 py-2 text-xs font-black ${done ? "bg-white text-emerald-700" : "bg-violet-700 text-white"}`}
                >
                  {locked ? "Locked" : "Done"}
                </span>
              </button>
            );
          })}
          {myDayIndex > 0 && (
            <button
              type="button"
              onClick={() => {
                setProgress((value) => ({
                  ...value,
                  morningDone: [],
                  dayIndex: 0,
                }));
                react("My Day is ready to start again.");
              }}
              className="mt-2 min-h-12 rounded-2xl border-2 border-violet-200 bg-violet-50 px-4 font-black text-violet-800"
            >
              Start My Day Again
            </button>
          )}
        </div>
      </div>
    </section>
  );

  const Help = (
    <section className="flex flex-1 flex-col gap-4">
      <div className="flex items-center justify-between">
        <BackButton />
        <h2 className="text-2xl font-black text-[#34264A]">I Need Help</h2>
      </div>
      <div className="grid flex-1 grid-cols-2 gap-3 md:grid-cols-4">
        {[
          ["help", "Help me", "Please help me"],
          ["tellSomeone", "Tell someone", "I need to tell someone"],
          ["ask", "I hurt", "Something hurts"],
          ["speak", "I don't understand", "I don't understand"],
        ].map(([icon, label, spoken]) => (
          <PrimaryTile
            key={label}
            title={label}
            subtitle="Tap to say it"
            tone="rose"
            onClick={() => react(spoken)}
          >
            <JnrPageIcon type={icon as JnrIconType} size="large" />
          </PrimaryTile>
        ))}
      </div>
    </section>
  );

  const breakChoices: Array<[string, string]> = [
    ["Quiet space", "/nixi-quiet-reading.png"],
    ["Slow breaths", "/nixi-slow-breaths.png"],
    ["Headphones", "/nixi-break-headphones.png"],
    ["Cuddle", "/nixi-cuddle-pink-adult.png"],
  ];
  const BreakScreen = (
    <section className="flex flex-1 flex-col gap-4">
      <div className="flex items-center justify-between">
        <BackButton />
        <h2 className="text-2xl font-black text-[#34264A]">I Need a Break</h2>
      </div>
      <div className="grid flex-1 grid-cols-2 gap-3 md:grid-cols-4">
        {breakChoices.map(([label, image]) => (
          <PrimaryTile
            key={label}
            title={label}
            subtitle="This can help"
            tone="mint"
            onClick={() => react(`${label} can help. I am here with you.`)}
          >
            <img
              src={image}
              alt=""
              className="h-full w-full object-contain p-1"
              draggable={false}
            />
          </PrimaryTile>
        ))}
      </div>
    </section>
  );

  const Feelings = (
    <section className="flex flex-1 flex-col gap-4">
      <div className="flex items-center justify-between">
        <BackButton />
        <div className="text-right">
          <h2 className="text-2xl font-black text-[#34264A]">Feelings</h2>
          <p className="text-sm font-bold text-slate-500">How do I feel?</p>
        </div>
      </div>
      <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-3">
        {(
          ["Happy", "Okay", "Sad", "Worried", "Angry", "Tired"] as Feeling[]
        ).map((feeling) => (
          <button
            key={feeling}
            type="button"
            aria-label={feeling}
            onClick={() => {
              setSelectedFeeling(feeling);
              if (feeling === "Happy") {
                react(
                  "Happy is a nice feeling! NIXI is happy too!",
                  null,
                  true,
                );
                return;
              }
              go(
                "regulation",
                `You feel ${feeling.toLowerCase()}. What can help?`,
              );
            }}
            className="flex min-h-[150px] flex-col items-center justify-center gap-2 rounded-[25px] border-2 border-violet-100 bg-white p-3 shadow-[0_7px_18px_rgba(61,45,92,.06)]"
          >
            <JnrPageIcon type="feelings" size="large" />
            <strong className="text-lg text-slate-800">{feeling}</strong>
          </button>
        ))}
      </div>
    </section>
  );

  const Regulation = (
    <section className="flex flex-1 flex-col gap-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setScreen("feelings")}
          className="min-h-12 rounded-2xl border-2 border-violet-200 bg-white px-4 font-black text-violet-800"
        >
          Back
        </button>
        <div className="text-right">
          <h2 className="text-2xl font-black text-[#34264A]">What can help?</h2>
          {selectedFeeling && (
            <p className="text-sm font-bold text-slate-500">
              I feel {selectedFeeling.toLowerCase()}.
            </p>
          )}
        </div>
      </div>
      <div className="grid flex-1 grid-cols-2 gap-3 md:grid-cols-4">
        {breakChoices.map(([label, image]) => (
          <PrimaryTile
            key={label}
            title={label}
            subtitle="Choose this"
            tone="mint"
            onClick={() => react(`${label} can help. I am here with you.`)}
          >
            <img
              src={image}
              alt=""
              className="h-full w-full object-contain p-1"
              draggable={false}
            />
          </PrimaryTile>
        ))}
      </div>
    </section>
  );

  const Rewards = (
    <section className="flex flex-1 flex-col gap-4">
      <div className="flex items-center justify-between">
        <BackButton />
        <div className="text-right">
          <h2 className="text-2xl font-black text-[#34264A]">My NIXI</h2>
          <p className="text-sm font-bold text-slate-500">We are a team</p>
        </div>
      </div>
      <div className="grid flex-1 gap-4 md:grid-cols-[1fr_1.05fr]">
        <div className="flex flex-col items-center justify-center rounded-[30px] border-2 border-violet-100 bg-gradient-to-b from-[#F9F4FF] to-[#FFF4FB] p-5 text-center">
          <div
            role="button"
            tabIndex={0}
            aria-label={`Play with ${progress.nixiName}`}
            onClick={() => {
              setNixiState("celebrate");
              setNixiExpression("excited");
              window.setTimeout(() => {
                setNixiState("idle");
                setNixiExpression("happy");
              }, 1800);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.currentTarget.click();
              }
            }}
            className="relative aspect-[55/67] w-full max-w-[22rem] overflow-hidden rounded-[26px] border-2 border-violet-100 bg-[#d9efff] shadow-[0_10px_28px_rgba(68,49,104,.08)]"
            data-feeding={feeding || undefined}
          >
            <NixiSoundVideo
              className="absolute inset-0 h-full w-full object-cover opacity-100"
              src="/nixi/nixi-profile-play.mp4"
              label={`${progress.nixiName} flying through a flower meadow`}
              muted
              preload="auto"
              revealAfterPlaybackStarts
            />
            <NixiSoundVideo
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-in-out ${feeding ? "opacity-100" : "pointer-events-none opacity-0"}`}
              src="/nixi/nixi-profile-feed.mp4"
              label={`${progress.nixiName} ready to eat NIXI bites`}
              muted={false}
              active={feeding}
              preload="auto"
              revealAfterPlaybackStarts
              mediaRef={feedVideoRef}
            />
            {feeding && (
              <span className="jnr-feeding-effects" aria-hidden="true">
                <i
                  key={feedingBiteKey}
                  className="jnr-food-bite jnr-food-bite--tap"
                />
              </span>
            )}
          </div>
          <p className="mt-2 text-xl font-black text-violet-950">{progress.nixiName}</p>
          <div className="mt-3 flex gap-2">
            <span className="inline-flex items-center rounded-full bg-white px-4 py-2 text-sm font-black text-violet-800">
              <NixiStarMark className="nixi-star-mark--reward" />
              {progress.points}
            </span>
            <span className="rounded-full bg-white px-4 py-2 text-sm font-black text-amber-800">
              Bites {progress.bites}
            </span>
          </div>
          <button
            type="button"
            disabled={feeding ? feedingBitesLeft < 1 : progress.bites < 5}
            onClick={feedNixi}
            className="mt-4 min-h-12 w-full max-w-xs rounded-2xl bg-violet-700 font-black text-white disabled:bg-slate-300"
          >
            {feeding
              ? feedingBitesLeft > 0
                ? `Send a bite · ${feedingBitesLeft} left`
                : "NIXI is finishing…"
              : "Feed NIXI · 5 bites"}
          </button>
          <NixiCookieJar bites={progress.bites} />
        </div>
        <div className="flex flex-col gap-4">
          {SHOW_NIXI_CHOOSER_IN_PROTOTYPE && (
          <div className="rounded-[27px] border-2 border-slate-100 bg-white p-5">
            <button
              type="button"
              onClick={() => setChooserOpen((value) => !value)}
              aria-expanded={chooserOpen}
              className="flex w-full items-center justify-between text-left"
            >
              <span>
                <span className="block text-sm font-black text-slate-700">Choose your NIXI</span>
                <span className="mt-1 block text-xs font-semibold text-slate-500">
                  {progress.variant === "girl" ? "Girl" : "Boy"} · {progress.colour} · tap to change
                </span>
              </span>
              <span className="text-lg font-black text-violet-700" aria-hidden="true">{chooserOpen ? "−" : "+"}</span>
            </button>
            {chooserOpen && <>
            <p className="mt-3 text-xs font-semibold text-slate-500">
              This choice belongs only to {childName}’s profile.
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {(
                [
                  ["girl", "Girl NIXI"],
                  ["boy", "Boy NIXI"],
                ] as Array<[NixiVariant, string]>
              ).map(([variant, label]) => (
                <button
                  key={variant}
                  type="button"
                  aria-pressed={progress.variant === variant}
                  onClick={() => {
                    setProgress((value) => ({ ...value, variant }));
                    react(label + " selected!");
                  }}
                  className={
                    "min-h-[74px] rounded-2xl border-2 bg-white p-3 text-sm font-black " +
                    (progress.variant === variant
                      ? "border-violet-600 bg-violet-50 text-violet-800"
                      : "border-slate-200 text-slate-500")
                  }
                >
                  <span className="block text-2xl" aria-hidden="true">
                    {variant === "girl" ? (
                      "♥"
                    ) : (
                      <NixiStarMark className="nixi-star-mark--choice" />
                    )}
                  </span>
                  {label}
                </button>
              ))}
            </div>
            <p className="mt-5 text-sm font-black text-slate-700">
              Choose NIXI colour
            </p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {(
                [
                  ["lavender", "Purple", "#9B82E1"],
                  ["pink", "Pink", "#EB63B1"],
                  ["mint", "Mint", "#46B8A6"],
                ] as Array<[NixiColour, string, string]>
              ).map(([colour, label, swatch]) => (
                <button
                  key={colour}
                  type="button"
                  onClick={() => {
                    setProgress((value) => ({ ...value, colour }));
                    react(`${label} NIXI!`);
                  }}
                  className={`min-h-[86px] rounded-2xl border-2 bg-white p-2 text-xs font-black ${progress.colour === colour ? "border-violet-600 text-violet-800" : "border-slate-200 text-slate-500"}`}
                >
                  <span
                    className="mx-auto mb-2 block h-9 w-9 rounded-full"
                    style={{ background: swatch }}
                  />
                  {label}
                </button>
              ))}
            </div>
            </>}
          </div>
          )}
          <div className="rounded-[27px] border-2 border-violet-100 bg-white p-5">
            <label htmlFor="nixi-name" className="text-sm font-black text-slate-700">
              NIXI’s name
            </label>
            <div className="mt-2 flex gap-2">
              <input
                id="nixi-name"
                value={nixiNameDraft}
                maxLength={18}
                onChange={(event) => setNixiNameDraft(event.target.value)}
                className="min-w-0 flex-1 rounded-xl border-2 border-violet-100 px-3 py-2 font-black text-violet-950 outline-none focus:border-violet-500"
              />
              <button
                type="button"
                onClick={() => {
                  const nixiName = nixiNameDraft.trim().slice(0, 18) || "NIXI";
                  setProgress((value) => ({ ...value, nixiName }));
                  setNotice(`${nixiName} likes their new name!`);
                }}
                className="rounded-xl bg-violet-700 px-3 text-sm font-black text-white"
              >
                Save
              </button>
            </div>
          </div>
          <div className="rounded-[27px] border-2 border-amber-100 bg-[#FFFDF6] p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-black text-amber-700">
                  My next reward
                </p>
                <h3 className="text-xl font-black text-slate-900">
                  Choose a NIXI sticker
                </h3>
              </div>
              <JnrPageIcon type="rewards" size="medium" />
            </div>
            <p className="mt-3 text-sm font-bold text-slate-600">
              {progress.points} of 10 points
            </p>
            <div className="mt-2 h-4 overflow-hidden rounded-full bg-white">
              <div
                className="h-full rounded-full bg-gradient-to-r from-violet-600 to-pink-500"
                style={{ width: `${Math.min(100, progress.points * 10)}%` }}
              />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {[5, 10, 15].map((goal) => (
                <div
                  key={goal}
                  className={`rounded-2xl border-2 p-3 text-center ${progress.points >= goal ? "border-amber-300 bg-white" : "border-slate-100 bg-slate-50 opacity-60"}`}
                >
                  <JnrPageIcon
                    type={
                      goal === 5
                        ? "activities"
                        : goal === 10
                          ? "feelings"
                          : "rewards"
                    }
                    size="small"
                  />
                  <p className="mt-1 text-xs font-black">{goal} points</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  const navItems: Array<{ screen: Screen; label: string; icon: JnrIconType }> =
    [
      { screen: "home", label: "Home", icon: "home" },
      { screen: "daytime", label: "Activities", icon: "activities" },
      { screen: "feelings", label: "Feelings", icon: "feelings" },
      { screen: "rewards", label: "My NIXI", icon: "rewards" },
    ];

  return (
    <div
      className="jnr-app min-h-screen bg-[#F5F4F8] p-2 sm:p-4"
      onFocusCapture={(event) => announceButton(event.target)}
      onPointerDownCapture={(event) => announceButton(event.target)}
    >
      <main className="mx-auto flex min-h-[calc(100vh-16px)] w-full max-w-[1180px] flex-col gap-4 rounded-[32px] border border-violet-100 bg-[#FEFDFE] p-3 pb-2 shadow-[0_20px_50px_rgba(52,44,72,.08)] sm:min-h-[calc(100vh-32px)] sm:p-5 sm:pb-3">
        {Header}
        {screen === "home" && Home}
        {screen === "morning" && Morning}
        {screen === "daytime" && Daytime}
        {screen === "help" && Help}
        {screen === "break" && BreakScreen}
        {screen === "feelings" && Feelings}
        {screen === "regulation" && Regulation}
        {screen === "rewards" && Rewards}
        <nav
          className="sticky bottom-0 z-20 grid grid-cols-4 gap-1 rounded-[24px] border border-violet-100 bg-white/95 p-2 shadow-[0_-8px_24px_rgba(70,48,112,.08)] backdrop-blur"
          aria-label="JNR main navigation"
        >
          {navItems.map((item) => {
            const active =
              screen === item.screen ||
              (item.screen === "daytime" && screen === "morning") ||
              (item.screen === "feelings" && screen === "regulation");
            return (
              <button
                key={item.label}
                type="button"
                onClick={() =>
                  item.screen === "rewards"
                    ? openNixiProfile()
                    : go(item.screen, item.label)
                }
                data-silent-speech={item.screen === "rewards" || undefined}
                className={`flex min-h-[70px] flex-col items-center justify-center gap-1 rounded-[18px] text-xs font-black transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-300 ${active ? "bg-[#F2EBFF] text-violet-800" : "text-slate-500"}`}
              >
                {item.screen === "rewards" ? (
                  <img
                    src="/nixi-tile-icon.webp"
                    alt=""
                    className="h-8 w-8 rounded-xl object-cover object-top"
                    draggable={false}
                  />
                ) : (
                  <JnrPageIcon type={item.icon} size="small" />
                )}
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
      </main>
      {celebrationTheme === "stars" &&
        screen === "morning" &&
        morningIndex < 0 && (
          <div className="jnr-nixi-flight-sweep" aria-hidden="true">
            <NixiCompanion
              variant={progress.variant}
              colour={progress.colour}
              state="celebrate"
              expression="proud"
              size="large"
              context="task"
              activity="flyComplete"
            />
          </div>
        )}
      {celebrationTheme && (
        <div
          className="jnr-celebration-sweep"
          data-theme={celebrationTheme}
          aria-hidden="true"
        >
          {Array.from({ length: 18 }, (_, index) => (
            <i
              key={index}
              style={{
                top: `${7 + ((index * 17) % 82)}%`,
                animationDelay: `${(index % 6) * 0.16}s`,
                fontSize: `${22 + (index % 4) * 6}px`,
              }}
            >
              {celebrationTheme === "bedtime" && index % 3 === 0 ? (
                "☾"
              ) : (
                <NixiStarMark className="nixi-star-mark--celebration" />
              )}
            </i>
          ))}
        </div>
      )}
      {parentOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 p-5">
          <div
            className="w-full max-w-md rounded-[28px] bg-white p-6 shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="parent-title"
          >
            <h2
              id="parent-title"
              className="text-2xl font-black text-violet-900"
            >
              Parent controls
            </h2>
            <p className="mt-3 text-sm font-semibold leading-6 text-slate-600">
              Parent settings control the routines, help phrases, breaks and
              rewards shown in this JNR view.
            </p>
            <button
              type="button"
              onClick={openLillianProfile}
              className="mt-5 min-h-12 w-full rounded-2xl bg-violet-700 font-black text-white"
            >
              Return to Lillian’s profile
            </button>
            <button
              type="button"
              onClick={() => {
                setParentOpen(false);
                setHold(0);
              }}
              className="mt-3 min-h-12 w-full rounded-2xl border-2 border-violet-200 bg-white font-black text-violet-800"
            >
              Stay in {childName}’s app
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

